import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import type { ApiClient } from './client';
import { createApiClient } from './index';

const ApiContext = createContext<ApiClient | null>(null);

/**
 * Provides the active {@link ApiClient}, wired to the auth layer so the real
 * client gets a fresh token per request. Must sit inside AuthProvider.
 */
export function ApiProvider({ children }: { children: ReactNode }): JSX.Element {
  const { getAccessToken } = useAuth();
  const client = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

/** Access the active API client. Throws if used outside {@link ApiProvider}. */
export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error('useApi must be used within an ApiProvider.');
  }
  return client;
}
